import React, { useEffect } from "react";
import CheckoutModal from "./components/CheckoutModal";
import { useCategoryState } from "../hooks/category/useCategoryState";
import { useDebounce } from "../hooks/useDebounce";
import { useCategoryData } from "../hooks/useCategoryData";
import { useProductData } from "../hooks/useProductData";
import { useAppearance } from "../hooks/useAppearance";
import { useDiscountHandler } from "../hooks/category/useDiscountHandler";
import { useCartSave } from "../hooks/category/useCartSave";
import LoadingScreen from "../../../../../shared/components/LoadingScreen";
import CategorySection from "../CategorySection";
import ProductGrid from "../ProductGrid/ProductGrid";
import OrderSummary from "../OrderSummary/OrderSummary";
import { useCartManagement } from "../hooks/orderSummary/useCartManagement";
import ProductCategories from "../ProductCategories";
import { Productbtn } from "../../../../../shared/services/interface";
import { useOrderProcessing } from "../Payment/hooks/useOrderProcessing";
import { ToastContainer } from "react-toastify";
import { useDeliveryFeeHandler } from "../hooks/category/useDeliveryFeeHandler";
import { useUser } from "src/business-types/grocery/components/UserContext";

function Category() {
  const { searchQuery, setSearchQuery, categoryID, setCategoryId, cartId, setCartId, modal, setModal, resetCategoryState  } = useCategoryState();
  const { prvDelete } = useOrderProcessing();
  const filteredTerm = useDebounce(searchQuery, 800);
  const { buttonNames, loading: categoriesLoading } = useCategoryData();
  const { selectedProducts, loading: productsLoading } = useProductData(categoryID || "", filteredTerm);
  const {
    selectedProductslist,
    valueMap,
    totalQuantity,
    totalAmount,
    condition,
    dis_value,
    setCondition,
    deliveryFee,
    grandTotal,
    setDeliveryFee,
    addProductToCart,
    removeProductFromCart,
    updateQuantity,
    resetCart,
  } = useCartManagement();
  const { background } = useAppearance();
  const { userConfigration } = useUser();
  const [activeProductId, setActiveProductId] = React.useState<string | null>(null);

  useEffect(() => {
    if (prvDelete) {
      resetCart();
    }
  }, [prvDelete]);

  const handleProductSelect = (product: Productbtn) => {
    addProductToCart(product);
    setActiveProductId(product._id.$oid);
  };
  const handleDiscountChange = useDiscountHandler(totalAmount, setCondition);
  const deliveryFeeHandler = useDeliveryFeeHandler(totalAmount, setDeliveryFee);
  const handleDeliveryFeeChange = (value: number) => {
    const event = { target: { value: value.toString() } } as React.ChangeEvent<HTMLInputElement>;
    deliveryFeeHandler(event);
  };
  const Savecart = useCartSave({ condition, dis_value, totalAmount, totalQuantity, deliveryFee, selectedProductslist, valueMap, setCartId });

  const loading = categoriesLoading || productsLoading;

  const toBoolean = (value: any) =>value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);

  const backHome = (data: any) => {
    if (data === "false") setModal(!modal);
    if (data === "done") resetAll();
  };

  const handleDone = () => {
    resetAll();
    setModal(false);
  };

  const Checkout = () => {
    Savecart("close");
    if (totalAmount) {
      setTimeout(() => setModal(!modal), 100);
    }
  };

  const resetAll = () => {
    resetCategoryState();
    resetCart();
    setActiveProductId(null);
  };

  return (
    <div className='w-full h-full overflow-hidden bg-white'>
      {loading && <LoadingScreen />}
      <div className='hidden lg:flex flex-row h-full overflow-auto bg-white'>
        <div className='lg:w-4/6 w-full p-2'>
          <CategorySection searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          <div className='thin-scrollbar flex flex-row gap-2 w-full mt-1 h-[80.99vh] overflow-auto'>
            <ProductCategories
              categories={buttonNames}
              selectedCategoryId={categoryID}
              backgroundColor={background}
              onCategorySelect={setCategoryId}
            />
            <ProductGrid
              products={selectedProducts}
              categorySelected={!!categoryID}
              onProductSelect={handleProductSelect}
              selectedProduct={activeProductId}
            />
          </div>
        </div>
        <OrderSummary
          selectedProducts={selectedProductslist}
          valueMap={valueMap}
          totalQuantity={totalQuantity}
          totalAmount={totalAmount}
          condition={condition}
          dis_value={dis_value}
          onQuantityChange={updateQuantity}
          onProductRemove={(id, amount) => {
            removeProductFromCart(id, amount);
            // CLEAR HIGHLIGHT WHEN THIS PRODUCT IS REMOVED
            if (activeProductId === id) {
              setActiveProductId(null);
            }
          }}
          onDiscountChange={handleDiscountChange}
          onCheckout={Checkout}
          onNewOrder={resetAll}
          onDeliveryFeeChange={handleDeliveryFeeChange}
          deliveryFee={deliveryFee}
          grandTotal={grandTotal}
          deliveryFeeEnabled={deliveryFeeEnabled}
        />
      </div>

      {/* Mobile layout can be similarly extracted into CategoryLayoutMobile */}
      <div className='flex flex-col lg:hidden h-[calc(100vh-64px)] overflow-hidden'>
        <div className='flex-1 overflow-y-auto p-2 space-y-4'>
          <CategorySection searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          <div className='flex gap-2'>
            <div className='w-[90px] shrink-0'>
              <ProductCategories
                categories={buttonNames}
                selectedCategoryId={categoryID}
                backgroundColor={background}
                onCategorySelect={setCategoryId}
              />
            </div>
            <div className='flex-1 overflow-x-auto'>
              <ProductGrid
                products={selectedProducts}
                categorySelected={!!categoryID}
                onProductSelect={handleProductSelect}
                selectedProduct={activeProductId}
              />
            </div>
          </div>
          <OrderSummary
            selectedProducts={selectedProductslist}
            valueMap={valueMap}
            totalQuantity={totalQuantity}
            totalAmount={totalAmount}
            condition={condition}
            dis_value={dis_value}
            onQuantityChange={updateQuantity}
            onProductRemove={(id, amount) => {
              removeProductFromCart(id, amount);
              if (activeProductId === id) {
                setActiveProductId(null);
              }
            }}
            onDiscountChange={handleDiscountChange}
            onCheckout={Checkout}
            onNewOrder={resetAll}
            onDeliveryFeeChange={handleDeliveryFeeChange}
            deliveryFee={deliveryFee}
            grandTotal={grandTotal}
            deliveryFeeEnabled={deliveryFeeEnabled}
          />
        </div>
      </div>

      <CheckoutModal modal={modal} cartId={cartId} backHome={backHome} onDone={handleDone} />
      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />
    </div>
  );
}

export default Category;
